import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DxSchedulerModule} from 'devextreme-angular/ui/scheduler';
import {DxButtonModule} from 'devextreme-angular/ui/button';
import {DxToolbarModule} from 'devextreme-angular/ui/toolbar';
import {DxTooltipModule} from 'devextreme-angular/ui/tooltip';
import {DxCheckBoxModule} from 'devextreme-angular/ui/check-box';
import {
    EmployeesClient, GetSchedulerEventsQuery,
    SchedulerEventStatus, SchedulerEventDto, SchedulerEventType, ThemeService
} from "@services";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {DxSchedulerComponent} from "devextreme-angular";
import {BehaviorSubject, map} from "rxjs";
import {QuickViewPanelComponent} from "../../../components/library/quick-view-panel/quick-view-panel.component";
import {UserAvatarEmailComponent} from "../../../components/utils/user-avatar-email/user-avatar-email.component";
import {DatePipe} from "@angular/common";
import {DxoToolbarComponent} from "devextreme-angular/ui/nested";
import {FilterPanelComponent} from "../../../components/library/filter-panel/filter-panel.component";
import {ToDatePipe} from "../../../pipes/to-date.pipe";

class SchedulerEvent extends SchedulerEventDto {
    text: string;
    allDay: boolean;
    startDate: Date;
    endDate: Date;
    displayStatus?: string | null;
}

type FilterModel = {
    selectedStatuses: SchedulerEventStatus[];
}

@Component({
    selector: 'app-schedule',
    standalone: true,
    imports: [
        CommonModule,
        DxSchedulerModule,
        DxButtonModule,
        DxToolbarModule,
        DxTooltipModule,
        DxCheckBoxModule,
        CapitalCasePipe,
        QuickViewPanelComponent,
        UserAvatarEmailComponent,
        DatePipe,
        DxoToolbarComponent,
        FilterPanelComponent,
        ToDatePipe
    ],
    templateUrl: './schedule.component.html'
})
export class ScheduleComponent implements OnInit {
    private themeService = inject(ThemeService);
    private employeesClient = inject(EmployeesClient);

    @ViewChild(DxSchedulerComponent, {static: false}) scheduler: DxSchedulerComponent;

    @Input() employeeId: string;
    @Input() offset: number = 100;
    @Input() embedded: boolean = false;

    currentDate: Date = new Date();
    events$: BehaviorSubject<SchedulerEvent[]> = new BehaviorSubject<SchedulerEvent[]>([]);
    private rawData: SchedulerEvent[] = [];

    isQuickViewPanelOpened = false;
    isFilterPanelOpened: boolean = false;
    selectedAppointment: SchedulerEvent | null = null;

    allStatuses = Object.values(SchedulerEventStatus);
    filterModel: FilterModel = {
        selectedStatuses: Object.values(SchedulerEventStatus)
    };

    statusResources = [
        {
            fieldExpr: 'displayStatus',
            dataSource: [] as any[],
            label: 'Status'
        }
    ];

    protected readonly SchedulerEventStatus = SchedulerEventStatus;
    protected readonly SchedulerEventType = SchedulerEventType;

    ngOnInit(): void {
        this.themeService.isDark$.subscribe(() => {
            this.initResourceColors();
        });

        this.loadSchedulerData();
    }

    onAppointmentClick(e: any) {
        e.cancel = true;
        this.selectedAppointment = e.appointmentData;
        this.isQuickViewPanelOpened = true;
    }

    onPinnedChange() {
        this.scheduler.instance.repaint();
    }

    onOptionChanged(e: any) {
        if (e.name === 'currentDate') {
            this.currentDate = e.value;
            this.loadSchedulerData();
        }
    }

    loadSchedulerData() {
        const {from, to} = this.getDateRange(this.currentDate);
        let query = new GetSchedulerEventsQuery({from, to, employeeId: this.employeeId});
        this.employeesClient.getSchedulerEvents(query).pipe(
            map((data: SchedulerEventDto[]) => data.map(event => {
                const dateParts = new Date(event.date);
                const localDate = new Date(dateParts.getUTCFullYear(), dateParts.getUTCMonth(), dateParts.getUTCDate());

                let displayStatus: string | null = event.type;
                if (event.type === SchedulerEventType.Status) {
                    displayStatus = SchedulerEventStatus[event.status];
                }

                return {
                    ...event,
                    text: event.label,
                    allDay: true,
                    startDate: localDate,
                    endDate: localDate,
                    displayStatus: displayStatus
                } as SchedulerEvent
            }))
        ).subscribe(data => {
            this.rawData = data;
            this.filterEvents();
        });
    }

    getDateRange(currentDate: Date) {
        const from = new Date(currentDate);
        const to = new Date(currentDate);

        from.setDate(1);
        to.setMonth(to.getMonth() + 1);
        to.setDate(0);

        return {from, to};
    }

    initResourceColors() {
        const getStyle = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();

        this.statusResources[0].dataSource = [
            {id: 'Scheduled', text: 'Scheduled', color: getStyle('--bs-primary-bg-subtle')},
            {id: 'TimeOff', text: 'Time Off', color: getStyle('--bs-secondary-bg-subtle')},
            {id: 'OnTime', text: 'On Time', color: getStyle('--bs-success-bg-subtle')},
            {id: 'ArrivedLate', text: 'Late', color: getStyle('--bs-warning-bg-subtle')},
            {id: 'Exception', text: 'Exception', color: getStyle('--bs-danger-bg-subtle')},
            {id: 'Absent', text: 'Absent', color: getStyle('--bs-info-bg-subtle')}
        ];
    }

    getEventData = (data: SchedulerEvent) => data;

    getEventClass(data: SchedulerEvent) {
        if (data.type === SchedulerEventType.Scheduled) return 'text-primary';
        if (data.type === SchedulerEventType.TimeOff) return 'text-secondary';

        switch (data.status) {
            case SchedulerEventStatus.OnTime:
                return 'text-success';
            case SchedulerEventStatus.ArrivedLate:
                return 'text-warning';
            case SchedulerEventStatus.Exception:
                return 'text-danger';
            case SchedulerEventStatus.Absent:
                return 'text-info';
            default:
                return '';
        }
    }

    toggleFilters = () => {
        this.isFilterPanelOpened = true;
    }

    isStatusSelected(status: SchedulerEventStatus): boolean {
        return this.filterModel.selectedStatuses.includes(status);
    }

    onStatusChanged(status: SchedulerEventStatus, e: any) {
        if (e.value) {
            if (!this.filterModel.selectedStatuses.includes(status)) {
                this.filterModel.selectedStatuses.push(status);
            }
        } else {
            this.filterModel.selectedStatuses = this.filterModel.selectedStatuses.filter(s => s !== status);
        }
        this.filterEvents();
    }

    filterEvents() {
        const filtered = this.rawData.filter(event =>
            event.status === null || this.filterModel.selectedStatuses.includes(event.status)
        );
        this.events$.next(filtered);
    }

    applyFilters = () => {
        this.filterEvents();
    };

    clearFilters = () => {
        this.filterModel.selectedStatuses = Object.values(SchedulerEventStatus);
        this.filterEvents();
    }
}
